import { Component, type ErrorInfo, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createBrowserRouter } from "react-router";

import { ApiClientError } from "@re-platform/api-client";
import { clearSession } from "./features/auth/session";
import { routes } from "./router";

function redirectToLogin() {
  clearSession();
  window.location.replace("/login?reason=expired");
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      // Don't retry 401s — that just generates 3× the noise when the session
      // is expired. One attempt, then surface the error / redirect.
      retry: (failureCount, error) => {
        if (error instanceof ApiClientError && error.status === 401) {
          redirectToLogin();
          return false;
        }
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
    },
  },
});

// Created on first render rather than at import time. It used to exist so a
// one-shot "/" → "/b/:slug" rewrite in main.tsx could run first; a brokerage's
// `/` now renders in place (Home, spec 011), so nothing depends on the order —
// it is simply harmless to keep.
let _router: ReturnType<typeof createBrowserRouter> | null = null;
function getRouter() {
  if (!_router) _router = createBrowserRouter(routes);
  return _router;
}

interface ErrorBoundaryState {
  error: Error | null;
}

class AppErrorBoundary extends Component<
  { children: ReactNode },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[AppErrorBoundary]", error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            padding: 32,
            fontFamily: "monospace",
            maxWidth: 700,
            margin: "40px auto",
          }}
        >
          <h1 style={{ color: "var(--rp-color-danger)", marginBottom: 12 }}>
            Application error
          </h1>
          <pre
            style={{
              background: "var(--rp-color-danger-bg)",
              border: "1px solid var(--rp-color-danger)",
              borderRadius: 8,
              padding: 16,
              overflowX: "auto",
              whiteSpace: "pre-wrap",
              color: "var(--rp-color-text)",
            }}
          >
            {this.state.error.message}
            {"\n\n"}
            {this.state.error.stack}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

export function App() {
  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={getRouter()} />
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}
